import React, { useState } from "react";
import { Link } from "react-router-dom";
import logo from "../../assets/usjp-logo__1_-removebg-preview.png";
import AuthShell from "../../components/auth/AuthShell";
import ServerUnavailableNotice from "../../components/auth/ServerUnavailableNotice";
import { systemForgotPassword } from "../../services/systemAuthService";

// Public "forgot password" entry point for the back-office (system user)
// login, reached from AdminLogin's "Forgot password?" link. Mirrors
// AdminLogin/ChangePassword's visual language (AuthShell, same branded card,
// same input styling).
//
// POST /api/system/forgot-password always returns the same generic message
// whether or not the email matches a real system user account (so this UI
// can't be used to enumerate accounts) — there is deliberately no
// success/failure branching here, just a single confirmation state once the
// request completes. A real match gets emailed a temporary password and is
// forced into must_change_password, so logging in with it (AdminLogin) lands
// straight on /admin/change-password same as any other first login.
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [serverUnavailable, setServerUnavailable] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setError("");
    setServerUnavailable(false);
    setSubmitting(true);
    try {
      const msg = await systemForgotPassword(email.trim());
      setMessage(msg || "If a system user account exists for this email, a temporary password has been sent.");
      setSent(true);
    } catch (err) {
      if (err?.isServerUnavailable) {
        setServerUnavailable(true);
      } else {
        const status = err?.response?.status;
        const msg =
          err?.response?.data?.message ||
          (status === 422 ? "Enter a valid email address." : "Could not send the reset email. Please try again.");
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full border rounded-xl px-4 py-4 text-lg focus:outline-none focus:ring-2 transition";

  return (
    <AuthShell>
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 lg:p-12 w-full">
          <div className="flex items-center gap-3 sm:gap-5 mb-6 sm:mb-8">
            <img src={logo} alt="USJ Logo" className="w-16 h-16 sm:w-24 sm:h-24 object-contain flex-shrink-0" />
            <div>
              <p className="font-semibold text-gray-800 text-sm sm:text-xl leading-tight whitespace-nowrap">
                University of Sri Jayewardenepura
              </p>
              <p className="text-sm sm:text-lg text-blue-600">Sports Facility — Admin Portal</p>
            </div>
          </div>

          {sent ? (
            <>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-center text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Check Your Email</h2>
              <p className="text-center text-gray-500 text-lg mb-8">{message}</p>
              <Link
                to="/admin/login"
                className="w-full inline-flex items-center justify-center bg-blue-700 hover:bg-blue-800 active:bg-blue-900 text-white font-semibold py-4 rounded-xl transition text-lg shadow-sm hover:shadow-md"
              >
                Back to Login
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-center text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Forgot Password</h2>
              <p className="text-center text-gray-500 text-lg mb-8">
                Enter your email and we'll send you a temporary password.
              </p>

              {serverUnavailable && (
                <ServerUnavailableNotice onRetry={handleSubmit} retrying={submitting} />
              )}

              <form onSubmit={handleSubmit} noValidate>
                <label htmlFor="forgot-email" className="block text-lg font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@sjp.ac.lk"
                  className={`${inputBase} mb-4 ${
                    error ? "border-red-400 focus:ring-red-400 bg-red-50" : "border-gray-300 focus:ring-blue-500"
                  }`}
                />

                {error && (
                  <p className="text-red-500 text-sm mt-2 mb-4 flex items-center gap-1">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting || !email}
                  className="w-full bg-blue-700 hover:bg-blue-800 active:bg-blue-900 disabled:bg-blue-300 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl transition mt-2 text-lg shadow-sm hover:shadow-md"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Sending…
                    </span>
                  ) : (
                    "Send Temporary Password"
                  )}
                </button>

                <Link
                  to="/admin/login"
                  className="block w-full text-center text-sm font-medium text-gray-400 hover:text-gray-600 mt-4 transition"
                >
                  Back to Login
                </Link>
              </form>
            </>
          )}
        </div>

        <p className="text-base text-gray-500 text-center mt-8">
          © {new Date().getFullYear()} University of Sri Jayewardenepura
        </p>
      </div>
    </AuthShell>
  );
}
